import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import type { ExecuteResult, ExecuteStreamEvent, TestPlan } from "@/services/testing";
import type { PlanExecutionPreviewModel } from "@/utils/testing/planPreview";
import { PlanStatusBadge } from "./PlanStatusBadge";
import { collectQueryDescriptions } from "./typedQuery";

export function ExecutionPlanCards({
	plan,
	preview,
	running,
	events,
	runResult,
}: {
	plan: TestPlan;
	preview: PlanExecutionPreviewModel;
	running: boolean;
	events: ExecuteStreamEvent[];
	runResult: ExecuteResult | null;
}) {
	const currentTitle = [...events]
		.reverse()
		.find((e): e is { type: "step"; phase: "begin"; title: string } => e.type === "step" && e.phase === "begin")
		?.title;
	const failed = runResult?.summary.first_failure?.plain_english ?? runResult?.summary.first_failure?.kind ?? null;
	// Data assertions carry a structured `query` (or a legacy `soql`). The regex-derived preview
	// can't see either, so surface them alongside the inferred checks — that IS what gets verified.
	const irBody = plan.steps as { ir?: unknown } | undefined;
	const queryChecks = collectQueryDescriptions(irBody?.ir);
	const checks = [...queryChecks, ...preview.checks.filter((c) => !queryChecks.includes(c))];
	const cards = [
		{ key: "setup", title: "Setup data", values: preview.setup, icon: "data" },
		{ key: "login", title: "Login", values: preview.login, icon: "setup" },
		{ key: "pages", title: "Pages", values: preview.pages, icon: "setup" },
		{ key: "clicks", title: "Actions", values: preview.clicks, icon: "action" },
		{ key: "fields", title: "Fields", values: preview.fields, icon: "data" },
		{ key: "checks", title: "Checks", values: checks, icon: "check" },
		{ key: "cleanup", title: "Cleanup", values: preview.cleanup, icon: "data" },
	];
	return (
		<div className="rounded-md border border-primary/30 bg-card/80 p-1.5 text-foreground shadow-sm">
			<div className="mb-1.5 flex flex-wrap items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="flex items-center gap-2">
						<PlanStatusBadge status={plan.status} />
						<div className="truncate text-sm font-semibold">{plan.name}</div>
					</div>
					{plan.intent && (
						<div className="mt-1 text-xs text-muted-foreground">{plan.intent}</div>
					)}
				</div>
			</div>
			<div className="grid gap-1 group-data-[cw-md]/chat:grid-cols-2">
				{cards.map((card) => (
					<ExecutionPlanCard
						key={card.key}
						title={card.title}
						values={card.values}
						kind={card.icon}
						running={running && Boolean(currentTitle && card.values.some((v) => currentTitle.toLowerCase().includes(v.toLowerCase().slice(0, 24))))}
						failed={Boolean(failed && card.values.some((v) => failed.toLowerCase().includes(v.toLowerCase().slice(0, 24))))}
						passed={runResult?.status === "passed"}
					/>
				))}
			</div>
			{preview.unknowns.length > 0 && (
				<div className="mt-1 rounded-md border border-warning/40 bg-warning/10 p-1.5 text-xs text-warning-strong">
					Needs clarification: {preview.unknowns.join("; ")}
				</div>
			)}
		</div>
	);
}

export function ExecutionPlanCard({
	title,
	values,
	kind,
	running,
	failed,
	passed,
}: {
	title: string;
	values: string[];
	kind: string;
	running: boolean;
	failed: boolean;
	passed: boolean;
}) {
	const statusIcon = failed ? (
		<XCircle className="h-3.5 w-3.5 text-destructive-strong" />
	) : running ? (
		<Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
	) : passed ? (
		<CheckCircle2 className="h-3.5 w-3.5 text-success-strong" />
	) : (
		<span className="h-2 w-2 rounded-full bg-muted-foreground/40" />
	);
	return (
		<div
			className={`rounded-md border p-1 text-xs ${
				failed
					? "border-destructive/40 bg-destructive/10"
					: running
						? "border-primary/60 bg-primary/10 shadow-[0_0_0_1px_hsl(var(--primary)/0.18)]"
						: "border-border bg-muted/20"
			}`}
		>
			<div className="mb-1 flex items-center gap-1.5">
				{statusIcon}
				<span className="font-semibold text-foreground">{title}</span>
				<span className="ml-auto shrink-0 rounded bg-card px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
					{kind}
				</span>
			</div>
			{values.length > 0 ? (
				<ul className="space-y-0.5">
					{values.slice(0, 5).map((value) => (
						<li key={value} className="truncate text-muted-foreground" title={value}>
							{value}
						</li>
					))}
				</ul>
			) : (
				<div className="text-muted-foreground/70">No explicit items declared.</div>
			)}
			{running && (
				<div className="mt-2 h-1 overflow-hidden rounded-full bg-primary/20">
					<div className="h-full w-2/3 animate-pulse rounded-full bg-primary" />
				</div>
			)}
		</div>
	);
}
